'use client'

import { useCart } from '@shopify/hydrogen-react'
import { useEffect, useRef } from 'react'

import { env } from '@/env'

import { toStorefrontCart, toStorefrontProductFromCartLine } from './Analyzify.adapters'
import { isAnalyzifyEnabled } from './Analyzify.constants'
import { useAnalyzifyTracking } from './Analyzify.useTracking'

import type { CartLine } from '@shopify/hydrogen-react/storefront-api-types'

type LineQuantities = Map<string, { quantity: number; line: CartLine }>

type Baseline = { cartId: string; lines: LineQuantities }

/** What changed on one line: the line itself and how many were added or removed. */
type LineChange = { line: CartLine; quantity: number }

export const toLineQuantities = (lines: CartLine[]): LineQuantities =>
  new Map(lines.flatMap((line) => (line?.id ? ([[line.id, { quantity: line.quantity ?? 0, line }]] as const) : [])))

/**
 * Per-line difference between two settled carts, matched on line id.
 *
 * Unchanged lines produce nothing, which is also what a failed update looks
 * like. A line that is gone is only knowable from the previous snapshot; a line
 * that merely shrank is read from the current one, so it carries the price the
 * customer is paying now.
 */
export const diffCartLines = (previous: LineQuantities, current: LineQuantities) => {
  const added: LineChange[] = []
  const removed: LineChange[] = []

  current.forEach(({ quantity, line }, id) => {
    const delta = quantity - (previous.get(id)?.quantity ?? 0)

    if (delta > 0) added.push({ line, quantity: delta })
  })

  previous.forEach(({ quantity, line }, id) => {
    const remaining = current.get(id)
    const delta = quantity - (remaining?.quantity ?? 0)

    if (delta > 0) removed.push({ line: remaining?.line ?? line, quantity: delta })
  })

  return { added, removed }
}

/**
 * Fires add_to_cart and remove_from_cart with the quantity that changed.
 *
 * hydrogen-react reports that the cart updated, not what changed, so the change
 * is recovered by diffing the settled cart against the previous settled cart,
 * line by line. A line going from 2 to 3 is one add, not three; an update that
 * only rewrites line attributes - how LPG pricing reaches Cart Transform - is no
 * change at all and reports nothing.
 *
 * This covers Stock Holding reorders for free: they go through the same cart via
 * linesAdd, so the diff picks them up like any other add.
 */
const Tracker = () => {
  const cart = useCart()
  const { ready, addToCart, removeFromCart } = useAnalyzifyTracking()
  // Null until the first settled cart. A cart restored from the cookie after
  // hydration is the starting point, not a basket of adds.
  const baseline = useRef<Baseline | null>(null)

  const status = cart?.status
  const cartId = cart?.id ?? ''
  const lines = (cart?.lines ?? []) as CartLine[]
  const fingerprint = `${cartId}|${lines.map((line) => `${line?.id}:${line?.quantity}`).join(',')}`

  useEffect(() => {
    if (status !== 'idle' || !cart) return

    const current = toLineQuantities(lines)
    const previous = baseline.current

    // A different cart replaced the one being watched (a new location creates a
    // new cart, checkout clears it). Not something the shopper did to a line.
    if (previous && previous.cartId && previous.cartId !== cartId) {
      baseline.current = { cartId, lines: current }

      return
    }

    if (!previous) {
      baseline.current = { cartId, lines: current }

      return
    }

    // Hold the baseline until the SDK can report, so nothing is lost in between.
    if (!ready) return

    const storefrontCart = toStorefrontCart(cart)
    const { added, removed } = diffCartLines(previous.lines, current)

    // Argument order is load-bearing: addToCart(product, quantity, cart, variantId).
    added.forEach(({ line, quantity }) => addToCart(toStorefrontProductFromCartLine(line), quantity, storefrontCart, line.merchandise?.id))
    removed.forEach(({ line, quantity }) => removeFromCart(toStorefrontProductFromCartLine(line), quantity, storefrontCart, line.merchandise?.id))

    baseline.current = { cartId, lines: current }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, status, cartId, fingerprint])

  return null
}

/** Fires add_to_cart / remove_from_cart. Renders nothing. */
export const AnalyzifyCartTracker = () => {
  if (!isAnalyzifyEnabled(env.NEXT_PUBLIC_ANALYZIFY_MODE)) return null

  return <Tracker />
}
